import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const METHODS = ["Bank Transfer", "Card", "Cheque", "Cash"];
const today = () => new Date().toISOString().slice(0, 10);

function useSubmit(onDone, onOpenChange) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); onOpenChange(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return [busy, run];
}

export function RefundCreditDialog({ open, onOpenChange, cm, onDone }) {
  const [f, setF] = useState({});
  const [busy, run] = useSubmit(onDone, onOpenChange);
  useEffect(() => { if (open) setF({ amount: String(cm?.credit_remaining || 0), method: "Bank Transfer", date: today(), reference: "" }); }, [open, cm]);
  const amt = Number(f.amount || 0);
  const valid = amt > 0 && amt <= (cm?.credit_remaining || 0) + 0.001;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund customer credit</DialogTitle>
          <DialogDescription>{cm?.doc_no} · {cm?.customer_name} · credit remaining {money(cm?.credit_remaining || 0)}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} className="rounded-sm" data-testid="refund-amount" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-sm" data-testid="refund-date" /></div>
          <div>
            <Label className="text-xs">Method</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger className="rounded-sm" data-testid="refund-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Reference</Label><Input value={f.reference || ""} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="e.g. bank ref" className="rounded-sm" data-testid="refund-reference" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button disabled={!valid || busy} onClick={() => run(() => api.post(`/sales-orders/${cm.id}/refund`, { ...f, amount: amt }), `Refund of ${money(amt)} recorded`)} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="refund-confirm">
            {busy ? "Saving…" : "Record Refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApplyCreditDialog({ open, onOpenChange, cm, onDone }) {
  const [inv, setInv] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, run] = useSubmit(onDone, onOpenChange);
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["credit-open-invoices", cm?.id],
    queryFn: async () => (await api.get(`/sales-orders/${cm.id}/open-invoices`)).data,
    enabled: open && !!cm?.id, staleTime: 0,
  });
  useEffect(() => { if (!open) { setInv(null); setAmount(""); } }, [open]);
  const pick = (i) => { setInv(i); setAmount(String(Math.min(i.outstanding, cm?.credit_remaining || 0))); };
  const cap = inv ? Math.min(inv.outstanding, cm?.credit_remaining || 0) : 0;
  const amt = Number(amount || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="apply-credit-dialog">
        <DialogHeader>
          <DialogTitle>Apply credit to an invoice</DialogTitle>
          <DialogDescription>{cm?.doc_no} · credit remaining {money(cm?.credit_remaining || 0)}</DialogDescription>
        </DialogHeader>
        {isLoading ? <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
          : invoices.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center" data-testid="apply-no-invoices">This customer has no open invoices.</p>
          : (
            <div className="max-h-56 overflow-y-auto border border-border rounded-sm divide-y divide-border">
              {invoices.map((i) => (
                <button key={i.id} onClick={() => pick(i)} data-testid={`apply-invoice-${i.doc_no}`}
                  className={`w-full flex justify-between px-3 py-2 text-sm text-left transition-colors ${inv?.id === i.id ? "bg-primary/10" : "hover:bg-secondary/60"}`}>
                  <span><span className="font-mono text-xs font-semibold">{i.doc_no}</span><span className="block text-[11px] text-muted-foreground">{i.date}{i.due_date ? ` · due ${i.due_date}` : ""}</span></span>
                  <span className="text-right text-xs tabular"><span className="block font-semibold">{money(i.outstanding)} owing</span><span className="text-muted-foreground">of {money(i.total)}</span></span>
                </button>
              ))}
            </div>
          )}
        {inv && <div><Label className="text-xs">Amount to apply (max {money(cap)})</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-sm" data-testid="apply-amount" /></div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button disabled={!inv || amt <= 0 || amt > cap + 0.001 || busy} onClick={() => run(() => api.post(`/sales-orders/${cm.id}/apply-credit`, { invoice_id: inv.id, amount: amt }), `${money(amt)} applied to ${inv.doc_no}`)} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="apply-credit-confirm">
            {busy ? "Applying…" : "Apply Credit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
